import { useMemo } from "react"

import type { IVariantSummary } from "~/hooks/useVariantTableData"
import {
  seasonLineageRequirement,
  seasonRouteRequirement,
  type SeasonLineageGap,
} from "~/utils/demandSeasonTrendMode"
import { type ForecastBreakdown, getForecastBreakdown } from "~/utils/forecast-alert-utils"
import {
  type AuthoringLevel,
  INSIGHT_PREFIX,
  type InsightOffer,
  type InsightScalars,
  type NoosMetricKey,
} from "~/utils/demandInsightValues"
import { aggregateSalesVsLy } from "~/utils/forecast-decomposition"
import { formatNumber } from "~/utils/formatting"
import { roundNumber } from "~/utils/helpers"
import { formatName } from "~/utils/namingUtils"

/**
 * NOOS demand-info breakdown as toggleable rows, keyed so enabled state persists per variant.
 * Values and offer-gating are server-side (`~/utils/demandInsightValues`); this file owns only the
 * labels and detail strings.
 */

export type { NoosMetricKey }

type SalesVsLy = ReturnType<typeof aggregateSalesVsLy>

export type NoosAdjustmentRow = {
  key: NoosMetricKey
  label: string
  detail?: string
  beta?: boolean
  explainer?: string
  value: number
}

const DISCOUNTS_EXPLAINER =
  "Part of what the comparable variants sold, they sold because it was discounted. Their average discount on reorders becomes the units it added, at a price elasticity of 1.75. Removing those units lowers the plan."

const PARTIAL_HISTORY_EXPLAINER =
  "The baseline sums 52 weeks without dividing, so a variant that started selling part-way through counts as a short year. Its own weekly rate is applied to the weeks before its first sale. Variants selling in under half their weeks are excluded, as are wholesale channels with a forward order book."

// Keyed off the same `isNpi` branch the values take.
const describeInsight = (
  key: NoosMetricKey,
  isNpi: boolean,
  s: InsightScalars,
  salesVsLy: SalesVsLy,
): Omit<NoosAdjustmentRow, "key" | "value"> => {
  switch (key) {
    case "npiForecastedDemand":
      return { label: "Forecasted demand", detail: "Model forecast beyond known preorders" }
    case "preOrderChange":
      return {
        label: `${formatName("preorder", false, true)} momentum`,
        detail: `Committed ${formatName("preorder", true)} vs baseline: ${formatNumber(s.futurePreOrders)} vs ${formatNumber(s.baselineRealisedPreorders ?? 0)}`,
      }
    case "stockout":
      // On fashion it is measured on the comparables in their season, so no weeks count applies.
      return {
        label: "Estimated demand during stockout",
        detail:
          s.kind === "fashion"
            ? `${formatName("sale", true, true)} the comparable variants lost to stockouts in their collection`
            : `${formatName("sale", true, true)} lost to stockouts in the weeks the baseline reads`,
      }
    case "missingSales":
      return { label: "Estimated un-observed demand" }
    // Not `missingSales` (no-data weeks inside the history): these are weeks before it starts.
    case "partialHistory":
      return {
        label: `Estimated demand before first ${formatName("sale")}`,
        beta: true,
        explainer: PARTIAL_HISTORY_EXPLAINER,
      }
    case "manualAdjustment":
      return isNpi
        ? { label: "Manual input" }
        : { label: "Manual adjustment", detail: "Configured via NOOS demand plan" }
    case "excludedFromForecast":
      return {
        label: `${formatName("sale", true, true)} not used for demand`,
        detail: "Past sales excluded from the base used to estimate demand",
      }
    case "fashionForecast":
      return {
        label: "Fashion Demand",
        detail: "Borrowed from fashion model for early weeks",
      }
    // Label only: the figure is the level's, so a per-variant sentence would present one variant's
    // number as the collection's. Named by source: against `modelTrend`, the source IS the choice.
    case "salesTrend":
      return { label: `Growth from your ${formatName("sale", true)}` }
    // Label only, same rule as `salesTrend`.
    case "seasonTrend":
      return { label: "Growth from comparable collections" }
    // Label only: the engine sizes it beside `computeInsightValues` (`insightOffers.ts`).
    case "preorderDevelopment":
      return { label: `${formatName("preorder", false, true)} development` }
    case "modelTrend": {
      const horizons = (
        [
          ["5W", salesVsLy.salesVsLy1mPct],
          ["13W", salesVsLy.salesVsLy3mPct],
          ["26W", salesVsLy.salesVsLy6mPct],
          ["52W", salesVsLy.salesVsLy12mPct],
        ] as const
      )
        .filter(([, pct]) => pct != null)
        .map(([label, pct]) => `${label} ${pct! > 0 ? "+" : ""}${roundNumber(pct!, 1)}%`)
      return {
        label: "Growth from the forecast",
        detail:
          horizons.length > 0
            ? `${formatName("sale", true, true)} vs LY: ${horizons.join(" · ")}`
            : `Recent ${formatName("sale", true)} vs the same period last year`,
      }
    }
    case "aiImpact":
      return {
        label: "Other model signals",
        detail: "The part of the forecast the demand trend doesn't explain",
      }
    case "discounts":
      return {
        label: "Discount impact on demand",
        // Volume-weighted average DEPTH, not the share of discounted units (`discountQuantity` is
        // Σ units × discount fraction).
        detail:
          s.discountShare != null
            ? `Comparable reorders sold at ${formatNumber(s.discountShare * 100, 1)}% off on average`
            : undefined,
        beta: true,
        explainer: DISCOUNTS_EXPLAINER,
      }
    case "phasingOut":
      return {
        label: "Phase-out impact",
        detail: "Sell-down capped to remaining stock",
      }
    default:
      return { label: key }
  }
}

// No `partialHistoryWeeks`, so the label-only path renders that row without its detail.
const NO_SCALARS: InsightScalars = {
  futurePreOrders: 0,
}
const NO_SALES_VS_LY = aggregateSalesVsLy([])

/** For a key no variant has a row for yet, e.g. while the sizing reads are in flight. */
export const insightLabelFor = (key: NoosMetricKey): string =>
  describeInsight(key, false, NO_SCALARS, NO_SALES_VS_LY).label

/**
 * Why a supported insight has nothing to apply here, naming the data it is measured from. A
 * function, not a map: `formatName` reads the tenant's naming at call time.
 */
export const insightRequirementFor = (
  key: NoosMetricKey,
  levelName: string,
  /** `seasonTrend` only: each missing link of the chain has a different fix. */
  season?: {
    unavailable: SeasonLineageGap | null
    /** False on a member of a group-linked season (`seasonTrendApplicable`). */
    applicable?: boolean
    thisName?: string | null
    comparableName?: string | null
    groupName?: string | null
    priorName?: string | null
    running?: readonly { name: string; end: string | null }[]
  } | null,
): string => {
  const sales = formatName("sale", true)
  const preorders = formatName("preorder", true)
  switch (key) {
    case "salesTrend":
      return `This ${levelName} has no comparable 12-month ${sales} history, so there is no trend to apply.`
    case "seasonTrend":
      // A resolved chain offering nothing is a data state (no baseline), not a missing decision.
      if (season?.unavailable == null) {
        if (season?.applicable === false) return seasonRouteRequirement(levelName, season.groupName)
        return `No variant in this ${levelName} has a baseline for the growth rate to apply to.`
      }
      return seasonLineageRequirement({
        gap: season.unavailable,
        levelName,
        thisName: season.thisName,
        comparableName: season.comparableName,
        priorName: season.priorName,
        running: season.running,
      })
    case "modelTrend":
      return `The model's forecast tracks last year's ${sales}, so there is no trend to separate out.`
    case "aiImpact":
      return "The model reports no trend signal for these variants."
    case "preOrderChange":
      return `No committed ${preorders} in the next 52 weeks, so there is no change to apply.`
    case "unknownPreOrders":
      return `The model expects no further ${preorders} on these variants.`
    // Both kinds measure on what the baseline stands on, so they say the same.
    case "preorderDevelopment":
      return `No ${preorders} on this ${levelName}'s baseline, and none booked yet, so there is nothing to project.`
    case "stockout":
      return "No stockouts were identified in the baseline."
    case "missingSales":
      return `No gaps in these variants' ${sales} history to compensate for.`
    // "enough" covers all four gates; naming one would be wrong for the rest.
    case "partialHistory":
      return `No variants here have enough part-year ${sales} history to scale up.`
    case "discounts":
      return "No discounted reorders were identified in the baseline."
    case "phasingOut":
      return `No variants in this ${levelName} are phasing out.`
    case "npiForecastedDemand":
      return `No new variants in this ${levelName}, so there is no matched forecast to apply.`
    case "npiKnownPreorders":
      return `No new variants with committed ${preorders} in this ${levelName}.`
    case "manualAdjustment":
      return "No forecast adjustments have been made on these variants."
    case "excludedFromForecast":
      return "Nothing is excluded from these variants' forecast."
    case "fashionForecast":
      return "No collection forecast covers these variants."
  }
}

/** Three tiers: one level, one lower level, or every lower level. */
export const authoringLevelLabelFor = (
  level: AuthoringLevel,
): { label: string; tip: string; tier: 1 | 2 | 3 } => {
  switch (level) {
    case "overall":
      return {
        label: "Overall only",
        tip: "Set on the highest level only — one setting across all channels, warehouses and markets.",
        tier: 1,
      }
    case "warehouse":
      return {
        label: "Per warehouse",
        tip: "Can be set per warehouse. Other warehouses keep the overall setting.",
        tier: 2,
      }
    case "channel":
      return {
        label: "Per channel",
        tip: "Can be set per channel. Other channels keep the overall setting.",
        tier: 2,
      }
    case "market":
      return {
        label: "Per market",
        tip: "Can be set per market. Other markets keep the overall setting.",
        tier: 2,
      }
    case "all":
      return {
        label: "All levels",
        tip: "Can be set on every lower level — warehouse, channel and market. The most flexible, and the most complex.",
        tier: 3,
      }
  }
}

export type InsightContribution = { variantId: string; label: string; units: number }

export type InsightFold = {
  key: NoosMetricKey
  label: string
  units: number
  /** Level-scoped keys only. */
  levelUnits?: number
  /** Variants that OFFERED the key (what `levelUnits` was split across), not the full count. */
  variantCount: number
  contributions: InsightContribution[]
}

/** The one home of the Σ rule, label source and level-figure rule. */
export const foldInsightOffers = (
  variants: readonly IVariantSummary[],
  offersFor: (variantId: string) => InsightOffer[] | null,
): Map<NoosMetricKey, InsightFold> => {
  const byKey = new Map<NoosMetricKey, InsightFold>()
  for (const v of variants) {
    for (const o of offersFor(v.variantId) ?? []) {
      let fold = byKey.get(o.key)
      if (!fold) {
        fold = {
          key: o.key,
          label: insightLabelFor(o.key),
          units: 0,
          // Identical on every offering variant, so the first wins rather than being summed.
          levelUnits: o.basis === "level" ? o.levelUnits : undefined,
          variantCount: 0,
          contributions: [],
        }
        byKey.set(o.key, fold)
      }
      fold.units += o.units
      fold.variantCount += 1
      fold.contributions.push({
        variantId: v.variantId,
        label: v.variantDisplayName,
        units: o.units,
      })
    }
  }
  for (const fold of byKey.values()) {
    fold.contributions.sort((a, b) => Math.abs(b.units) - Math.abs(a.units))
  }
  return byKey
}

/**
 * The whole level's figure (`fetchLevelCollectionIds` resolves the GROUP), identical on member and
 * group pages; variants are sized from their own baselines and need not sum to it.
 */
export const levelSliceDetail = (levelUnits: number): string =>
  `This level's ${formatNumber(Math.round(levelUnits))} units. Each variant is sized from its own baseline, so the rows here need not add up to it.`

export const insightRowsFromOffers = (
  offers: readonly InsightOffer[],
  scalars: InsightScalars | null,
  isNpi: boolean,
  salesVsLy: SalesVsLy,
): NoosAdjustmentRow[] =>
  offers.map((o) => ({
    key: o.key,
    ...describeInsight(o.key, isNpi, scalars ?? NO_SCALARS, salesVsLy),
    value: o.units,
  }))

/**
 * `applied` already means the collection-wide row reaches this variant unoverridden. The value is
 * the engine's attribution (what the key did), never the offer (what it would do); 0 when nothing
 * was charged.
 */
export const collectionWideRowsFromOffers = (
  offers: readonly InsightOffer[],
  scalars: InsightScalars | null,
  isNpi: boolean,
  salesVsLy: SalesVsLy,
  setupAttribution: Record<string, number> | undefined,
): NoosAdjustmentRow[] =>
  offers
    .filter((o) => o.applied)
    .map((o) => ({
      key: o.key,
      ...describeInsight(o.key, isNpi, scalars ?? NO_SCALARS, salesVsLy),
      value: setupAttribution?.[`${INSIGHT_PREFIX}${o.key}`] ?? 0,
    }))

// Committed pre-orders are demand the plan must cover. Backs the UI warning for a plan (e.g. a
// manual target) that drops below the committed band.
export const coversKnownPreorders = (totalDemand: number, knownPreorders: number): boolean =>
  Math.round(totalDemand) >= Math.round(knownPreorders)

/**
 * Per-variant NOOS breakdown off the engine's offer list, so inspector cards never size a variant
 * in the browser. `sizing` while offers are in flight: `null` is not "offers nothing".
 */
export const useNoosDecomposition = (
  variant: IVariantSummary,
  offers: InsightOffer[] | null,
  scalars: InsightScalars | null,
  isNpi: boolean,
  setupAttribution: Record<string, number> | undefined,
): {
  components: NoosAdjustmentRow[]
  collectionRows: NoosAdjustmentRow[]
  collectionRowKeys: ReadonlySet<string>
  breakdown: ForecastBreakdown
  sizing: boolean
} =>
  useMemo(() => {
    const salesVsLy = aggregateSalesVsLy([variant])
    const collectionRows = collectionWideRowsFromOffers(
      offers ?? [],
      scalars,
      isNpi,
      salesVsLy,
      setupAttribution,
    )
    return {
      components: insightRowsFromOffers(offers ?? [], scalars, isNpi, salesVsLy),
      collectionRows,
      collectionRowKeys: new Set(collectionRows.map((r) => r.key)),
      breakdown: getForecastBreakdown([variant]),
      sizing: offers == null,
    }
  }, [variant, offers, scalars, isNpi, setupAttribution])
